import styled from "@emotion/styled";
import { size } from "polished";
import React, { useRef } from "react";

import useAnalytics from "@brexhq/next/src/hooks/useAnalytics";
import { localStorage } from "@brexhq/next/src/utils/localStorage";

import { StyledVideoProps } from "./types";

const StyledVideo = styled.video<{
  aspectRatio?: number;
  maxWidth?: string;
  border?: boolean;
}>(({ aspectRatio, maxWidth, theme, border }) => ({
  width: "100%",
  objectFit: "cover",
  display: "block",
  [`@media (min-width: ${theme.breakpoints.SML}px)`]: {
    maxWidth: maxWidth, // Apply maxWidth only for non-mobile screens
  },
  aspectRatio: aspectRatio ? `1 / ${aspectRatio}` : "auto",
  border: border ? `1px solid ${theme.colors.border.default}` : "none",
}));

const Fallback = styled.img({
  ...size("100%"),
  objectFit: "contain",
});

export const Video = React.forwardRef<HTMLVideoElement, StyledVideoProps>(
  function Video(
    {
      border,
      src,
      posterImageSrc,
      captionFile,
      captionRef,
      format = "mp4",
      shouldLazyLoad = false,
      aspectRatio,
      programName,
      maxWidth,
      ...rest
    },
    ref,
  ) {
    const videoSource = shouldLazyLoad ? (
      <source data-src={src} type={`video/${format}`} />
    ) : (
      <source src={src} type={`video/${format}`} />
    );

    const currentVideoTimestampRef = useRef<number>(0);

    const onVideoTimeUpdate = React.useCallback(
      (e: React.SyntheticEvent<HTMLVideoElement, Event>) => {
        currentVideoTimestampRef.current = e.currentTarget.currentTime;
      },
      [],
    );

    const getCurrentTimeStampInMinutes = React.useCallback(() => {
      return (currentVideoTimestampRef.current || 0) / 60;
    }, []);

    const { identify } = useAnalytics();

    // This hook is used to track webinar analytics. We keep track of the current video timestamp in the `currentTimestamp`
    // state variable. Once a lead exits the video/page/tab/window we send an `identify` event to Segment (and Marketo) with
    // the timestamp, video program name & the lead's email address.
    React.useEffect(() => {
      const captureTimeViewed = () => {
        const leadEmail = localStorage?.getItem("workEmail");
        const currentTimeStampInMinutes = getCurrentTimeStampInMinutes();
        if (!!programName && !!leadEmail && currentTimeStampInMinutes) {
          identify({
            properties: {
              email: JSON.parse(leadEmail),
              formFillUrl: window.location.href,
              partnerId: localStorage?.getItem("refCode"),
              recordingWatchedProgramName: programName,
              recordingWatchedLength: currentTimeStampInMinutes.toString(),
            },
            integrations: { "Marketo V2": true },
          });
        }
      };

      const beforeUnload = () => {
        captureTimeViewed();
      };

      window.addEventListener("beforeunload", beforeUnload);

      return () => {
        captureTimeViewed();
        window.removeEventListener("beforeunload", beforeUnload);
      };
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
      <StyledVideo
        muted
        border={border}
        ref={ref}
        {...rest}
        crossOrigin="anonymous"
        playsInline
        preload={shouldLazyLoad ? "none" : "auto"}
        poster={posterImageSrc ?? ""}
        aspectRatio={aspectRatio}
        onTimeUpdate={onVideoTimeUpdate}
        maxWidth={maxWidth ? maxWidth : undefined}
      >
        {videoSource}
        {posterImageSrc && <Fallback src={posterImageSrc} />}
        {captionFile && (
          <track
            ref={captionRef}
            label="English"
            kind="subtitles"
            srcLang="en"
            src={captionFile}
            default
          />
        )}
      </StyledVideo>
    );
  },
);
